import { Container, Particle, ParticleContainer } from "pixi.js";

/**
 * Une couche de particules additives, où une particule meurt en temps constant.
 *
 * # Pourquoi elle existe
 *
 * `ParticleContainer.removeParticle` cherche la particule (`indexOf`) puis
 * referme le trou (`splice`) : deux parcours du tableau par mort, alors qu'il
 * en meurt des dizaines par image sur un millier de vivantes. Ici la dernière
 * vient prendre la place de la morte — l'ordre de dessin change, et c'est sans
 * conséquence : le mélange est additif, la somme ne dépend pas de l'ordre.
 *
 * L'appelant tient son propre tableau d'états, indice par indice avec
 * `container.particleChildren`. Il naît et meurt des deux côtés ensemble.
 *
 * # Le tampon statique
 *
 * Pixi range les attributs en deux tampons : le dynamique, renvoyé au GPU à
 * chaque image, et le statique, renvoyé seulement quand le conteneur se dit
 * « sale ». Or ajouter ou retirer une particule le salit — donc, tant que ça
 * bougeait, les deux tampons repartaient à chaque image.
 *
 * Ici la couche ne se salit que lorsque la population dépasse son record :
 * en dessous, le tampon statique tient déjà des données valides pour tous les
 * indices dessinés. **Cela suppose que toutes les particules partagent la même
 * texture** — le statique ne porte plus que les coordonnées de texture, les
 * mêmes pour toutes, donc l'échange de deux indices ne le change pas.
 *
 * Tout ce qui varie en vol — position, rotation, taille, couleur — doit donc
 * être déclaré dynamique par l'appelant, `vertex` compris : c'est lui qui
 * porte l'échelle (il n'existe pas de propriété `scale` dans Pixi).
 */
export class ParticleLayer {
  readonly container: ParticleContainer;
  /** Population la plus haute déjà téléversée dans le tampon statique. */
  private uploaded = 0;

  constructor(parent: Container, dynamicProperties: Record<string, boolean>) {
    this.container = new ParticleContainer({ dynamicProperties });
    this.container.blendMode = "add";
    parent.addChild(this.container);
  }

  get size(): number {
    return this.container.particleChildren.length;
  }

  add(particle: Particle): void {
    this.container.particleChildren.push(particle);
  }

  /** Retire la particule d'indice `index`, remplacée par la dernière. */
  removeAt(index: number): void {
    const children = this.container.particleChildren;
    const last = children.length - 1;

    children[index] = children[last];
    children.length = last;
  }

  /**
   * Une fois par image, après les naissances et les morts : ne prévient Pixi
   * que si la population a dépassé son record, seul cas où le tampon statique
   * manque de données.
   */
  flush(): void {
    if (this.size > this.uploaded) {
      this.uploaded = this.size;
      this.container.update();
    }
  }

  clear(): void {
    this.container.particleChildren.length = 0;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
